<script lang="ts">
	/**
	 * Who gave up, in **three kinds** (§12.6).
	 *
	 * Reporting abandonment as one number is what makes it unactionable, and the three have
	 * different sizes, different causes and different fixes. Browse abandonment is enormous and
	 * mostly ordinary — the fix is upstream of the store. Flow abandonment is the actionable middle:
	 * they typed an email address. Payment abandonment is the expensive tail: they reached the
	 * provider's page. Summing them buries the third inside the first.
	 *
	 * Each states **what its rate is a fraction of**, in words, because three rates with three
	 * different denominators sitting in a row would otherwise be read as comparable.
	 *
	 * The conditions list is what makes this a fix rather than a statistic: "83 people abandoned" is
	 * a number, and "83 abandoned, 31 of them straight after `tax.countryMissing`" is a bug report.
	 */

	import {
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import { EmptyState, Panel, RelativeDate } from '../../../components';
	import type { AnalyticsAbandonment, AnalyticsQuery } from '../../../AppTypes';
	import { getAnalyticsAbandonment } from '../../../AppNetwork';
	import { createEventDispatcher } from 'svelte';

	export let filter: AnalyticsQuery;

	const dispatcher = createEventDispatcher<{ open: string }>();

	let data: AnalyticsAbandonment | null = null;
	let error = '';
	let loading = false;
	let expanded: string = '';

	$: queryKey = JSON.stringify(filter);
	$: void fnLoad(queryKey);

	async function fnLoad(_key: string): Promise<void> {
		loading = true;
		try {
			data = await getAnalyticsAbandonment(filter, 10);
			error = '';
		} catch (caught) {
			error = caught instanceof Error ? caught.message : 'Failed to load the abandonment report';
			data = null;
		} finally {
			loading = false;
		}
	}

	function fnPercent(fraction: number): string {
		return `${(fraction * 100).toFixed(fraction < 0.1 ? 1 : 0)}%`;
	}
</script>

<Panel title="Where people gave up" faIcon="person-walking-arrow-right">
	{#if error.length > 0}
		<EmptyState variant="error" title="The abandonment report could not be loaded." description={error} />
	{:else if data === null}
		<EmptyState variant="loading" />
	{:else}
		<div class="grid grid-cols-1 lg:grid-cols-3 gap-3" class:opacity-50={loading}>
			{#each data.kinds as kind (kind.key)}
				<div class="widget bg-depth min-w-0">
					<div class="card-label">{kind.label}</div>
					<p class="text-2xl font-bold tabular-nums mt-1">{kind.abandoned.toLocaleString()}</p>
					<p class="text-sm text-default">
						{fnPercent(kind.rate)} of {kind.denominator.toLocaleString()}
						{kind.denominatorLabel}
					</p>
					{#if kind.examples.length > 0}
						<button
							type="button"
							class="text-xs text-primary-600 dark:text-primary-400 hover:underline mt-2"
							on:click={() => (expanded = expanded === kind.key ? '' : kind.key)}
						>
							{expanded === kind.key ? 'Hide' : 'Show'} recent examples
						</button>
					{/if}
				</div>
			{/each}
		</div>

		{#each data.kinds as kind (kind.key)}
			{#if expanded === kind.key}
				<div class="overflow-x-auto mt-3">
					<Table class="dataTable">
						<TableHead>
							<TableHeadCell>Last seen</TableHeadCell>
							<TableHeadCell>Who</TableHeadCell>
							<TableHeadCell>Looking at</TableHeadCell>
							<TableHeadCell>Last page</TableHeadCell>
							<TableHeadCell>Last refusal</TableHeadCell>
						</TableHead>
						<TableBody>
							{#each kind.examples as example (example.sessionUUID)}
								<TableBodyRow>
									<TableBodyCell class="dataTable">
										<button
											type="button"
											class="text-primary-600 dark:text-primary-400 hover:underline"
											on:click={() => dispatcher('open', example.sessionUUID)}
										>
											<RelativeDate date={example.lastEventAt} />
										</button>
									</TableBodyCell>
									<!-- An anonymous visit is the common case, not a missing value. -->
									<TableBodyCell class="dataTable">
										{example.userEmail ?? 'Not signed in'}
										{#if example.country !== null}
											<span class="text-default text-xs block">
												{example.country} · {example.deviceType ?? 'unknown device'}
											</span>
										{/if}
									</TableBodyCell>
									<TableBodyCell class="dataTable">{example.productName ?? '—'}</TableBodyCell>
									<TableBodyCell class="dataTable">{example.lastPath ?? '—'}</TableBodyCell>
									<TableBodyCell class="dataTable">
										{#if example.lastErrorCondition !== null}
											<code class="text-xs">{example.lastErrorCondition}</code>
										{:else}
											—
										{/if}
									</TableBodyCell>
								</TableBodyRow>
							{/each}
						</TableBody>
					</Table>
				</div>
			{/if}
		{/each}

		<p class="text-default text-xs mt-3">
			{data.sessionsInProgress.toLocaleString()}
			{data.sessionsInProgress === 1 ? 'visit is' : 'visits are'} still in progress and counted in
			none of the three — a visit that has not gone idle yet has not given up.
		</p>

		{#if data.errorConditions.length > 0}
			<div class="mt-4">
				<div class="card-label mb-2">
					<i class="fa-solid fa-triangle-exclamation text-[11px] mr-1" />What the store refused
				</div>
				<p class="text-default text-xs mb-2">
					Every sentence a customer was shown in this range, by condition. This is usually where
					flow abandonment comes from.
				</p>
				<Table class="dataTable">
					<TableHead>
						<TableHeadCell>Condition</TableHeadCell>
						<TableHeadCell class="text-right">Times shown</TableHeadCell>
					</TableHead>
					<TableBody>
						{#each data.errorConditions as condition (condition.condition)}
							<TableBodyRow>
								<TableBodyCell class="dataTable"><code class="text-xs">{condition.condition}</code></TableBodyCell>
								<TableBodyCell class="dataTable text-right tabular-nums">
									{condition.count.toLocaleString()}
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					</TableBody>
				</Table>
			</div>
		{/if}
	{/if}
</Panel>
